import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import UpgradeModal from '../components/UpgradeModal';
import { toast } from 'sonner';
import { 
  Scale, Plus, Save, FolderPlus, MessageSquare, 
  ThumbsUp, ThumbsDown, X, Check, ArrowLeft
} from 'lucide-react';

const MaterialCompare = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [comparisonFields, setComparisonFields] = useState({ visual: [], technical: [] });
  const [canSave, setCanSave] = useState(false);
  const [planType, setPlanType] = useState('free');
  
  // Modal states
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showAddToBoardModal, setShowAddToBoardModal] = useState(false);
  const [showClientFeedback, setShowClientFeedback] = useState(false);
  
  // Form states
  const [comparisonName, setComparisonName] = useState('');
  const [userBoards, setUserBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [clientFeedback, setClientFeedback] = useState({ preferred: null, comment: '' });

  const materialIds = searchParams.get('ids')?.split(',').filter(Boolean) || [];

  const fetchComparison = useCallback(async () => {
    if (materialIds.length < 2) {
      toast.error('Seleziona almeno 2 materiali da confrontare');
      navigate('/materials');
      return;
    }

    try {
      const res = await axios.post(`${API}/compare/materials`, {
        material_ids: materialIds
      }, { withCredentials: true });

      setMaterials(res.data.materials || []);
      setComparisonFields(res.data.comparison_fields || { visual: [], technical: [] });
      setCanSave(res.data.can_save || false);
      setPlanType(res.data.plan_type || 'free');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.reason === 'comparison_limit' || detail?.reason === 'login_required') {
        setShowUpgrade(true);
      } else {
        toast.error('Errore nel caricamento del confronto');
      }
    } finally {
      setLoading(false);
    }
  }, [materialIds, navigate]);

  const fetchUserBoards = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/boards`, { withCredentials: true });
      setUserBoards(res.data.boards || []);
    } catch (err) {
      console.error('Error fetching boards:', err);
    }
  };

  useEffect(() => {
    fetchComparison();
    if (user) {
      fetchUserBoards();
    }
  }, [fetchComparison, user]);

  const handleSaveComparison = async () => {
    if (!canSave) {
      setShowUpgrade(true);
      return;
    }

    try {
      await axios.post(`${API}/compare/save`, {
        material_ids: materialIds,
        comparison_name: comparisonName || `Confronto ${new Date().toLocaleDateString('it-IT')}`
      }, { withCredentials: true });

      toast.success('Confronto salvato!');
      setShowSaveModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail?.upgrade_message || 'Errore nel salvataggio');
    }
  };

  const handleAddToBoard = async () => {
    if (!selectedBoard || !selectedMaterial) return;

    try {
      await axios.post(`${API}/compare/add-to-board`, {
        material_id: selectedMaterial,
        board_id: selectedBoard
      }, { withCredentials: true });

      toast.success('Materiale aggiunto alla board!');
      setShowAddToBoardModal(false);
      setSelectedMaterial(null);
      setSelectedBoard('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'aggiunta');
    }
  };

  const getVisualProfileValue = (material, key) => {
    const profile = material.visual_profile;
    if (!profile) return '-';
    
    const valueMap = {
      color_family: {
        white: 'Bianco', cream: 'Crema', beige: 'Beige', brown: 'Marrone',
        gray: 'Grigio', black: 'Nero', green: 'Verde', blue: 'Blu',
        pink: 'Rosa', red: 'Rosso', gold: 'Oro', multi: 'Multicolore'
      },
      pattern_type: {
        solid: 'Solido', veined: 'Venato', speckled: 'Puntinato', swirled: 'Sfumato',
        banded: 'A bande', mottled: 'Screziato', uniform: 'Uniforme', dramatic: 'Drammatico'
      },
      pattern_scale: {
        fine: 'Fine', medium: 'Medio', bold: 'Marcato', dramatic: 'Drammatico'
      },
      contrast_level: {
        low: 'Basso', medium: 'Medio', high: 'Alto', dramatic: 'Drammatico'
      },
      texture_type: {
        smooth: 'Liscio', polished: 'Lucido', honed: 'Levigato', brushed: 'Spazzolato',
        leathered: 'Leather', flamed: 'Fiammato', natural: 'Naturale'
      },
      visual_movement: {
        static: 'Statico', subtle: 'Leggero', moderate: 'Moderato', dynamic: 'Dinamico', dramatic: 'Drammatico'
      }
    };

    const value = profile[key];
    return valueMap[key]?.[value] || value || '-';
  };

  const getTechnicalValue = (material, key) => {
    if (key === 'finishes' || key === 'applications') {
      const val = material[key];
      if (Array.isArray(val)) return val.join(', ') || '-';
      return val || '-';
    }
    return material[key] || '-';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Scale className="w-6 h-6" />
                Confronta Materiali
              </h1>
              <p className="text-muted-foreground">
                {materials.length} materiali selezionati
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {user && (
              <Button 
                variant="outline" 
                onClick={() => canSave ? setShowSaveModal(true) : setShowUpgrade(true)}
                data-testid="save-comparison-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Salva Confronto
              </Button>
            )}
          </div>
        </div>

        {/* Comparison Grid */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Material Headers */}
          <div className={`grid grid-cols-${materials.length + 1} border-b`} style={{ gridTemplateColumns: `200px repeat(${materials.length}, 1fr)` }}>
            <div className="p-4 bg-gray-50 font-medium">Materiale</div>
            {materials.map((material) => (
              <div key={material.material_id} className="p-4 text-center border-l">
                <div className="aspect-square w-full max-w-[200px] mx-auto mb-3 rounded-lg overflow-hidden bg-gray-100">
                  {material.images?.[0] ? (
                    <img 
                      src={material.images[0]} 
                      alt={material.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      No Image
                    </div>
                  )}
                </div>
                <h3 
                  className="font-semibold cursor-pointer hover:text-primary"
                  onClick={() => navigate(`/materials/${material.material_id}`)}
                  data-testid={`material-name-${material.material_id}`}
                >
                  {material.name}
                </h3>
                <p className="text-sm text-muted-foreground">{material.stone_type}</p>
                
                <div className="flex justify-center gap-2 mt-3">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setSelectedMaterial(material.material_id);
                      setShowAddToBoardModal(true);
                    }}
                    data-testid={`add-to-board-${material.material_id}`}
                  >
                    <FolderPlus className="w-4 h-4 mr-1" />
                    Aggiungi
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Visual Characteristics Section */}
          <div className="border-b">
            <div className="p-3 bg-gray-100 font-semibold text-sm uppercase tracking-wide">
              Caratteristiche Visive
            </div>
            {comparisonFields.visual.map((field) => (
              <div 
                key={field.key}
                className="grid border-b last:border-b-0"
                style={{ gridTemplateColumns: `200px repeat(${materials.length}, 1fr)` }}
              >
                <div className="p-3 bg-gray-50 text-sm font-medium">{field.label}</div>
                {materials.map((material) => (
                  <div key={material.material_id} className="p-3 text-center border-l text-sm">
                    {getVisualProfileValue(material, field.key)}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Technical Characteristics Section */}
          <div>
            <div className="p-3 bg-gray-100 font-semibold text-sm uppercase tracking-wide">
              Caratteristiche Tecniche
            </div>
            {comparisonFields.technical.map((field) => (
              <div 
                key={field.key}
                className="grid border-b last:border-b-0"
                style={{ gridTemplateColumns: `200px repeat(${materials.length}, 1fr)` }}
              >
                <div className="p-3 bg-gray-50 text-sm font-medium">{field.label}</div>
                {materials.map((material) => (
                  <div key={material.material_id} className="p-3 text-center border-l text-sm">
                    {getTechnicalValue(material, field.key)}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Client Feedback Section (for presentation mode) */}
          {showClientFeedback && (
            <div className="p-4 bg-[var(--gold)]/10 border-t">
              <h4 className="font-medium mb-3">Feedback Cliente</h4>
              <div className="flex flex-wrap gap-2 mb-3">
                {materials.map((material) => (
                  <button
                    key={material.material_id}
                    onClick={() => setClientFeedback({ ...clientFeedback, preferred: material.material_id })}
                    className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                      clientFeedback.preferred === material.material_id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {clientFeedback.preferred === material.material_id && (
                      <Check className="w-4 h-4 inline mr-1 text-green-500" />
                    )}
                    {material.name}
                  </button>
                ))}
              </div>
              <Input
                placeholder="Aggiungi un commento..."
                value={clientFeedback.comment}
                onChange={(e) => setClientFeedback({ ...clientFeedback, comment: e.target.value })}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-center gap-4 mt-6">
          <Button 
            variant="outline" 
            onClick={() => navigate('/advisor')}
            data-testid="ask-advisor-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Chiedi al Material Advisor
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowClientFeedback(!showClientFeedback)}
          >
            {showClientFeedback ? 'Nascondi' : 'Mostra'} Feedback Cliente
          </Button>
        </div>
      </main>

      {/* Save Comparison Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Salva Confronto</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome Confronto</label>
                <Input
                  value={comparisonName}
                  onChange={(e) => setComparisonName(e.target.value)}
                  placeholder="Es: Opzioni cucina villa Rossi"
                  data-testid="comparison-name-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowSaveModal(false)}>
                  Annulla
                </Button>
                <Button onClick={handleSaveComparison} data-testid="confirm-save-btn">
                  Salva
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add to Board Modal */}
      {showAddToBoardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Aggiungi a Board</h2>
            <div className="space-y-4">
              {userBoards.length === 0 ? (
                <p className="text-muted-foreground">Non hai ancora creato board. <a href="/projects" className="text-primary hover:underline">Crea una board</a></p>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-1">Seleziona Board</label>
                  <select
                    value={selectedBoard}
                    onChange={(e) => setSelectedBoard(e.target.value)}
                    className="w-full border rounded-lg p-2"
                    data-testid="board-select"
                  >
                    <option value="">Seleziona...</option>
                    {userBoards.map((board) => (
                      <option key={board.board_id} value={board.board_id}>
                        {board.project_name || board.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setShowAddToBoardModal(false);
                  setSelectedMaterial(null);
                }}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleAddToBoard} 
                  disabled={!selectedBoard}
                  data-testid="confirm-add-btn"
                >
                  Aggiungi
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        feature="compare"
        message={planType === 'free' 
          ? "Passa a Pro per confrontare fino a 4 materiali e salvare i confronti"
          : "Effettua il login per salvare i tuoi confronti"
        }
      />

      <Footer />
    </div>
  );
};

export default MaterialCompare;
